import React from "react";
import { dispatch } from '@rematch/core';
import Graph from 'graph.js/dist/graph.full.js';
import { withRouter } from 'react-router-dom';
import { Grid, Button, Accordion, Icon, Input, Dimmer, Loader, Popup } from "semantic-ui-react";
import { toastr } from 'react-redux-toastr';

import { Form } from 'formsy-semantic-ui-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DateTime } from "luxon";
import moment from 'moment';

import Stack from 'stack-lifo';
import difference from 'lodash/difference';
import union from 'lodash/union';

import { durationValidation } from "../../../../common/RegexValidation";
import EllipsisTooltip from './../../../../common/EllipsisTooltip';
import GanttChart from '../../../../common/GanttChart';
import BreadCrumbs from './../../Drive/cards/Breadcrumbs';
import Labels from './../../../../common/Labels';
import LoadingComponent from "../../../../common/LoadingComponent";

import CreateInitiativeSteps from './Steps';
import GraphWorker from './graph.worker.js';

const DEFAULT_TIMEZONE = 'America/New_York';

const dayTimeLength = 24 * 60 * 60 * 1000;

//Array represents - MON, TUE, WED, THUS, FRI, SAT, SUN
// const dayFlags = [true, true, true, true, true, false, false];

let graph = new Graph();
let activityNodesMap = new Map();
let allGraphPaths = new Stack();

const advanceDateTime = function (startDateTime, startWeekDay, duration, dayFlags) {

    let currentDateTime = startDateTime;
    let currentWeekDay = startWeekDay;

    while (duration > 0) {
        currentDateTime += dayTimeLength;
        if (dayFlags[currentWeekDay]) {
            duration--;
        }
        currentWeekDay += 1;
        currentWeekDay = currentWeekDay <= 6 ? currentWeekDay : 0;
    }
    return currentDateTime;
}

const comparePaths = function (path1, path2) {
    const path1Parts = path1.split("-");
    const path2Parts = path2.split("-");

    if (path1Parts.length >= path2Parts.length) {
        return false;
    } else {
        const differece = difference(path2Parts, path1Parts);
        if (differece && differece.length === 1) {
            return true;
        } else {
            return false;
        }
    }

}

const createGraph = function (activities, parent, isSequential) {
    try {
        if (activities && activities.length > 0) {

            for (const [index, activity] of activities.entries()) {

                const graphObject = {
                    duration: activity.duration,
                    name: activity.name,
                    distanceFromStart: -1,
                    dependencies: activity.dependencies
                }
                activityNodesMap.set(activity._id.toString(), activity);
                graph.addVertex(activity._id.toString(), graphObject);

                if (parent) {
                    graph.addEdge(parent._id.toString(), activity._id.toString());
                }
                if (isSequential && (!activity.activities || (activity.activities && activity.activities.length === 0))) {
                    if (index !== 0 && parent && parent.activities[index - 1].isLeafNode) {
                        graph.addEdge(activity._id.toString(), parent.activities[index - 1]._id.toString());
                    }
                }
                if (activity.dependencies && activity.dependencies.length > 0) {

                    let depedenciesForAllParents = [];
                    //remove transitive depedencies
                    for (const dependency of activity.dependencies) {
                        if (!graph.hasVertex(dependency.toString())) {
                            graph.ensureVertex(dependency.toString(), { details: "temp ref" });
                        } else {
                            const currentParentDependencies = graph.vertexValue(dependency.toString()).dependencies;
                            depedenciesForAllParents = union(depedenciesForAllParents, currentParentDependencies);
                        }
                    }

                    const allRelevantDependencies = difference(activity.dependencies, depedenciesForAllParents);

                    for (const dependency of allRelevantDependencies) {
                        graph.addEdge(activity._id.toString(), dependency.toString())
                    }
                }
                createGraph(activity.activities, activity, isSequential);
            }
        }
    }
    catch (e) {
        throw e;
    }
}

const calculateDuration = function (pathData) {
    let totalDuration = 0;
    for (const pathEntry of pathData) {
        const vertexValue = graph.vertexValue(pathEntry);
        if (vertexValue) {
            if (vertexValue.duration) {
                totalDuration += Number(vertexValue.duration);
            }
        }
    }
    return totalDuration;
}

const getWorkingDay = function (timeZone, dayFlags) {
    let startDate = DateTime.local().startOf('day');
    let weekDay = startDate.weekday === 7 ? 0 : startDate.weekday;
    while (!dayFlags[weekDay % 6]) {
        startDate = startDate.plus({ days: 1 });
        weekDay = startDate.weekday === 7 ? 0 : startDate.weekday;
    }
    return startDate.toFormat('LL-dd-yyyy');
}

function traverseGraph(nodeId, dataString) {
    let count = 0;

    try {
        for (let [from] of graph.verticesTo(nodeId)) {
            count++;
            traverseGraph(from, `${dataString}-${from}`);
        }
    } catch (e) {
        throw e;
    }

    if (count === 0) {
        if (allGraphPaths.isEmpty()) {
            allGraphPaths.push(dataString);
        } else {
            //get last inserted item
            const lastInsertedPath = allGraphPaths.peek();
            if (comparePaths(lastInsertedPath, dataString)) {
                allGraphPaths.pop();
            }
            allGraphPaths.push(dataString);
        }
    }

}

class Finalize extends React.Component {

    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            loadingTimelineData: false,
            loadingGraph: false,
            initiativeData: null,
            contingency: 0,
            activeIndex: 1,
            counter: 0,
            isContingencyValid: true
        };
    }

    async componentDidMount() {
        dispatch.app.setRouteParams(this.props.match.params)
        dispatch.initiatives.setTabName("finalize");
        this._isMounted = true;

        this.worker = new GraphWorker();

        let timeZone = null;
        if (this.props.selectedOrganisation.timeZone) {
            timeZone = this.props.selectedOrganisation.timeZone;
        } else {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const payload = {
                organizationId: this.props.selectedOrganisation.id,
                timeZone
            }
            dispatch.organizationUnit.updateOrgTimeZone(payload);
        }

        let selectedDataById = this.props.selectedDataById;
        let objValidation = Object.entries(selectedDataById).length === 0 && selectedDataById.constructor === Object
        if (objValidation) {
            await dispatch.initiatives.getInDesignInitiativeDetailsById(this.props.match.params.initiativeId);

        }
        if (!this.props.selectedDataById.workingDays) {
            this.props.selectedDataById.workingDays = [false, true, true, true, true, true, false]
        }
        if (this.props.selectedDataById) {
            this.getDuration();
        }
        const startDate = this.props.selectedDataById.startDate ? moment(this.props.selectedDataById.startDate).format('MM-DD-YYYY') : getWorkingDay(timeZone, this.props.selectedDataById.workingDays);
        const contingency = this.props.selectedDataById.contingency ? parseInt(this.props.selectedDataById.contingency) : 0;
        this.calculateProjectEndDate(startDate, contingency);

        if (this._isMounted) {
            this.setState({
                startDate: startDate,
                contingency: contingency
            });
        }

    }

    componentWillUnmount() {
        this.worker.terminate();
        this._isMounted = false;
    }

    handleDateChangeRaw = (e) => {
        e.preventDefault();
    }

    getDuration = () => {
        graph = new Graph();
        activityNodesMap = new Map();
        createGraph(this.props.selectedDataById.activities, null, this.props.selectedDataById.isSequential);

        if (graph.cycle() && graph.cycle().length > 0) {
            this.props.selectedDataById.cycles = [];
            for (let cycle of graph.cycles()) {
                this.props.selectedDataById.cycles.push(cycle);
            }
            dispatch.initiatives.setInitiativeCycles(this.props.selectedDataById);
        } else {
            allGraphPaths = new Stack();
            for (let [key] of graph.sinks()) {
                traverseGraph(key, `${key}`);
            }

            let maxDuration = 0;
            while (!allGraphPaths.isEmpty()) {
                const graphPath = allGraphPaths.pop();
                let calculatedDuration = calculateDuration(graphPath.split("-"));
                if (calculatedDuration > maxDuration) {
                    maxDuration = calculatedDuration;
                }
            }
            this.props.selectedDataById.duration = maxDuration;
            dispatch.initiatives.setInitiativeDuration(maxDuration);
        }
    }

    calculateProjectEndDate(startDate, contingency) {
        let tempStartDate = null;
        if (typeof startDate === 'string') {
            tempStartDate = moment(startDate, 'MM-DD-YYYY');
        } else {
            tempStartDate = moment(startDate);
        }

        const day = tempStartDate.date();
        const month = tempStartDate.month() + 1;
        const year = tempStartDate.year();

        const timeZone = this.props.selectedOrganisation.timeZone ? this.props.selectedOrganisation.timeZone : DEFAULT_TIMEZONE;
        let projectStartDate = DateTime.fromObject({ year: year, month: month, day: day, zone: timeZone });

        while (!this.props.selectedDataById.workingDays[projectStartDate.weekday % 7]) {
            projectStartDate = projectStartDate.plus({ days: 1 });
        }

        if (this.props.selectedDataById.duration) {
            let duration = parseInt(this.props.selectedDataById.duration);

            let projectEndDate;
            const projectStartWeekDay = projectStartDate.weekday !== 7 ? projectStartDate.weekday : 0;

            if (contingency) {
                projectEndDate = advanceDateTime(projectStartDate.toMillis(), projectStartWeekDay, (duration + contingency), this.props.selectedDataById.workingDays);
            }
            else {
                projectEndDate = advanceDateTime(projectStartDate.toMillis(), projectStartWeekDay, duration, this.props.selectedDataById.workingDays);
            }

            const payLoad = {
                _id: this.props.selectedDataById._id,
                startDate: new Date(projectStartDate.startOf('day').toISO()),
                endDate: new Date(DateTime.fromMillis(projectEndDate - dayTimeLength).setZone(timeZone).endOf('day').toISO())
            }

            dispatch.initiatives.updateInitiativeBySpecificFields(payLoad);
            //dispatch.initiatives.setInitiativeStartEndDate(payLoad);

            this.setState({
                startDate: projectStartDate.startOf('day').toFormat('LL-dd-yyyy'),
                endDate: DateTime.fromMillis(projectEndDate - dayTimeLength).setZone(timeZone).endOf('day').toFormat('LL-dd-yyyy')
            });
        }
    }

    handleStartDateChange = (date) => {
        let tempDate = new Date(moment(date, 'DD-MM-YYYY'));
        let startDate = date;
        this.setState({
            startDate: startDate,
            activeIndex: 1
        });

        this.calculateProjectEndDate(tempDate, this.state.contingency);
    }

    contingencyValueChange = (event) => {

        this.setState({
            isContingencyValid:
                this.contingencyInput.props.isValidValue(event.target.value),
        });

        let contingency = 0;
        if (!isNaN(parseInt(event.target.value)) && this.contingencyInput.props.isValid()) {
            if ((parseInt(event.target.value)) > 0 && event.target.value.toString().length < 4 && event.target.value.toString().length > 0) {
                this.setState({
                    contingency: parseInt(event.target.value),
                    activeIndex: 1
                });
                contingency = parseInt(event.target.value);
                this.setState({
                    contingency: contingency,
                    activeIndex: 1
                });
            }
        } else {
            if (!isNaN(parseInt(event.target.value)) && !this.contingencyInput.props.isValid()) {
                if ((parseInt(event.target.value)) > 0 && event.target.value.toString().length < 4 && event.target.value.toString().length > 0) {
                    this.setState({
                        contingency: parseInt(event.target.value),
                        activeIndex: 1
                    });
                    contingency = parseInt(event.target.value);
                    this.setState({
                        contingency: contingency,
                        activeIndex: 1
                    });
                }
            } else {
                this.setState({
                    contingency: event.target.value,
                    activeIndex: 1
                });
            }
        }
        this.calculateProjectEndDate(this.state.startDate, contingency);
    }

    changeWorkingDay = async (day) => {
        if (this.props.selectedDataById.workingDays.filter(day => { return day }).length > 1 || !this.props.selectedDataById.workingDays[day]) {
            this.props.selectedDataById.workingDays[day] = !this.props.selectedDataById.workingDays[day];
            await dispatch.initiatives.updateTotalWorkingDaysInInitiative(this.props.selectedDataById);
            this.calculateProjectEndDate(this.state.startDate, this.state.contingency);
            this.setState({ activeIndex: 1 })
        }
        else {
            return;
        }

    }

    backClick = () => {
        let payload = {
            startDate: new Date(this.props.selectedDataById.startDate),
            endDate: new Date(this.props.selectedDataById.endDate),
            totaldays: this.props.selectedDataById.duration,
            _id: this.props.selectedDataById._id,
            contingency: this.state.contingency,
            teamId: this.props.selectedDataById.teamId
        }
        this.worker.terminate();
        dispatch.initiatives.updateInitiativeBySpecificFields(payload);

        const history = this.props.history;
        history.push({
            pathname: '/app/' + this.props.match.params.accountId + '/_ws/' + this.props.match.params.workspaceId + '/_in/indesign/configureActivity/' + this.props.match.params.initiativeId
        });
    }

    deleteInitiative = async () => {
        let initId = this.props.match.params.initiativeId;
        let payload = await Object.assign({}, this.props.selectedDataById);
        payload.initId = initId;
        let message = <div className="confirmMessage">
            <h3>Delete Initiative</h3>
            <label>{this.props.selectedDataById.name}</label>
            <p>Are you sure that you wish to do this?</p>
            <span >This action is not reversible</span>
        </div>
        await toastr.confirm(message, {
            onOk: () => {
                dispatch.initiatives.removeInitiative(payload);
            }
        });
    }

    next = () => {

        let payload = {
            startDate: new Date(this.props.selectedDataById.startDate),
            endDate: new Date(this.props.selectedDataById.endDate),
            totaldays: this.props.selectedDataById.duration,
            _id: this.props.selectedDataById._id,
            contingency: this.state.contingency,
            teamId: this.props.selectedDataById.teamId
        }

        this.worker.terminate();
        dispatch.initiatives.updateInitiativeBySpecificFields(payload);

        const history = this.props.history;
        history.push({
            pathname: '/app/' + this.props.match.params.accountId + '/_ws/' + this.props.match.params.workspaceId + '/_in/indesign/review/' + this.props.match.params.initiativeId
        });
    }

    handleClick = async (e, titleProps) => {

        const { index } = titleProps
        const { activeIndex } = this.state
        const newIndex = activeIndex === index ? -1 : index;
        this.setState({ activeIndex: newIndex })
        if (newIndex === 0) {
            this.setState({ loadingTimelineData: true });

            const data = {
                initiativeData: this.props.selectedDataById,
                startDate: this.state.startDate
            }

            this.worker.postMessage(data);
            this.worker.addEventListener('message', event => {
                const initiativeData = JSON.parse(event.data);
                dispatch.initiatives.setSelectedDataById(initiativeData)
                this.setState({
                    loadingTimelineData: false,
                    initiativeData: initiativeData
                });
            });
        }
    }
    cancelEdit = () => {
        let message = <div className="confirmMessage">
            <h3>Cancel Editing</h3>
            <label>{this.props.selectedDataById.name}</label>
            <p>Are you sure that you want to Cancel editing this initiative?</p>
            <span >All the changes done until now will be lost.</span>
        </div>
        toastr.confirm(message, {
            okText: 'Cancel Editing',
            cancelText: 'Keep Editing',
            onOk: () => {
                dispatch.initiatives.cancelInitiativeEdit(this.props.selectedDataById);
            }
        });


    }

    render() {
        const { activeIndex, loadingTimelineData, startDate, endDate } = this.state;

        let windowDimension = this.props.componentDesignerDimension;
        let designerHeight = (windowDimension.windowHeight - windowDimension.height) - 50
        let chartHeight = (windowDimension.windowHeight - windowDimension.height) - 400
        const errorLabel = <div className="customErrorLabel" />;

        let dateComponent = null;

        if (startDate) {
            if (typeof (startDate) === "string") {
                const startDateObj = moment(startDate, 'MM-DD-YYYY').toDate();
                dateComponent = <DatePicker
                    id="timeline-start-date"
                    selected={startDateObj}
                    onChange={this.handleStartDateChange}
                    onChangeRaw={this.handleDateChangeRaw}
                    dateFormat="MMM dd yyyy"
                    filterDate={(date) => {
                        const day = date.getDay();
                        return this.props.selectedDataById.workingDays[day];
                    }}
                    placeholderText="Date"
                    showMonthDropdown
                    showYearDropdown

                />

            }
        }

        let timelineComponent = null;

        if (loadingTimelineData) {
            timelineComponent = <Dimmer active inverted >
                <Loader size='small' >Preparing Timeline Graph</Loader>
            </Dimmer>
        } else {
            if (this.props.selectedDataById && this.props.selectedDataById.startDate) {

                timelineComponent = <GanttChart
                    timeZone={this.props.selectedOrganisation.timeZone}
                    activeIndex={activeIndex}
                    projectStartDate={this.state.startDate}
                    projectEndDate={this.state.endDate}
                    projectDetails={this.state.initiativeData}
                    windowDimension={windowDimension}
                    windowHeight={chartHeight}
                />
            }
        }

        return (
            <div ref={this.parentRef} style={{ width: "100%", maxHeight: designerHeight ? designerHeight : 210, minHeight: designerHeight ? designerHeight : 210, overflowY: "scroll" }}>
                {this.props.loadingDataById ? <LoadingComponent message="Please wait..." active={this.props.loadingDataById} /> : null}
                <Grid className="containerBoxFullWidth">
                    <Grid.Row >
                        <BreadCrumbs {...this.props} />
                    </Grid.Row>
                    <Grid.Row >
                        <CreateInitiativeSteps {...this.props} setPage="finalize" />
                    </Grid.Row>
                    <Grid.Row >
                        <Form className="w_100">
                            <Grid className="finalize">
                                <Grid.Row className="blocks">
                                    <Grid.Column width={2}>
                                        <Labels
                                            name={<strong className="final_label">Total Days</strong>}
                                            text={<div style={{ margin: "10px 0 0 0" }}>
                                                {this.props.selectedDataById.duration ? this.props.selectedDataById.duration : 0}
                                            </div>}
                                        />

                                    </Grid.Column>
                                    <Grid.Column width={3}>
                                        <div>
                                            <strong className="final_label"> Start Date</strong>
                                            {this.props.selectedDataById.status === 4 ? <Input
                                                disabled
                                                placeholder={moment(this.state.startDate, 'MM-DD-YYYY').format("MMM DD YYYY")}
                                                icon='calendar alternate outline' /> :
                                                <span className='timeLineDatePicker'>
                                                    {dateComponent}
                                                    <label htmlFor="timeline-start-date" className="timeline-start-date">
                                                        <Icon name='calendar alternate outline' className='timelineCalendarIcon' />
                                                    </label>
                                                </span>}
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column width={5}>
                                        <div className="days_buttons">
                                            <strong className="final_label"> Working Days</strong>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[0] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(0)}>Su</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[1] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(1)}>Mo</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[2] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(2)}>Tu</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[3] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(3)}>We</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[4] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(4)}>Th</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[5] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(5)}>Fr</Button>
                                            <Button size='mini' className={` ${this.props.selectedDataById.workingDays && this.props.selectedDataById.workingDays[6] ? "button-submit" : "button-cancel"}`} disabled={this.props.selectedDataById.status === 4} onClick={() => this.changeWorkingDay(6)}>Sa</Button>
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column width={3}>
                                        <div>
                                            <strong className="final_label" style={{ display: "flex" }}>
                                                <EllipsisTooltip className="float-left" name="Contingency / Holidays" style={{ width: "calc(100% - 16px )" }} />

                                                <Popup
                                                    trigger={<Icon name="exclamation circle" className="float-left" />}
                                                    content="This will add specified buffer days to initiative timeline."
                                                    position='top center'
                                                    className="popupStyledWhite"
                                                    inverted
                                                />
                                            </strong>
                                            <Form.Input
                                                name="contingency"
                                                id="contingency"
                                                innerRef={(c) => { this.contingencyInput = c; }}
                                                value={this.state.contingency}
                                                onChange={this.contingencyValueChange}
                                                validations={{ matchRegexp: durationValidation }}
                                                validationErrors={{
                                                    matchRegexp: 'No characters allowed. Maximum 3 digits allowed.',
                                                }}
                                                errorLabel={errorLabel}
                                                instantValidation
                                            />

                                        </div>
                                    </Grid.Column>
                                    <Grid.Column width={3}>
                                        <div>
                                            <strong className="final_label">Estimated End Date</strong>
                                            <Input disabled placeholder={moment(this.state.endDate, 'MM-DD-YYYY').format("MMM DD YYYY")} icon='calendar alternate outline' />
                                        </div>
                                    </Grid.Column>
                                </Grid.Row>

                                <Grid.Row >
                                    <Accordion style={{ margin: "0 0 20px 0", background: "#F3F7FB" }} className='w_100' styled>
                                        <Accordion.Title active={activeIndex === 0} index={0} onClick={this.handleClick}>
                                            <Icon name='dropdown' />
                                            Advanced Timeline - {this.props.selectedOrganisation.timeZone}
                                        </Accordion.Title>
                                        <Accordion.Content active={activeIndex === 0} style={{ padding: "20px 0 0 0" }}>
                                            {timelineComponent}
                                        </Accordion.Content>
                                    </Accordion>
                                </Grid.Row>


                                {this.props.selectedDataById.status === 0 || this.props.selectedDataById.status === 4 ?
                                    <Grid.Row>
                                        <Grid.Column width={8} className="pad_0">
                                            <div className="bottom_button_set" >
                                                <Button
                                                    onClick={this.backClick}
                                                    disabled={!this.state.isContingencyValid}
                                                    className="button-cancel"
                                                    type='button'>Previous </Button>
                                            </div>
                                        </Grid.Column >
                                        <Grid.Column width={8} className="pad_0">
                                            <div className="bottom_button_set" style={{ textAlign: "right" }} >
                                                {this.props.selectedDataById.status === 4 && <Button
                                                    type="button"
                                                    className="button-cancel"
                                                    onClick={() => this.cancelEdit()}>Cancel</Button>}
                                                <Button type='submit'
                                                    disabled={!this.state.isContingencyValid}
                                                    id='next'
                                                    className="button-submit"
                                                    onClick={this.next} > Next </Button>
                                            </div>
                                        </Grid.Column >
                                    </Grid.Row> : ""}
                            </Grid>
                        </Form>
                    </Grid.Row>
                </Grid>
            </div>
        );
    }
}



export default withRouter(Finalize);
